import { CheckIcon, CloseIcon, EditIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  ButtonGroup,
  Center,
  Editable,
  EditableInput,
  EditablePreview,
  Flex,
  Grid,
  Heading,
  IconButton,
  Input,
  Spinner,
  useEditableControls,
  useToast,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useMemo } from "react";
import { useParams } from "react-router";

import { Simulation, Event as ThreediEvent } from "@nens/threedi/openapi-client";

import SimulationsSvg from "@/assets/simulations.svg?url";

import { getSimulationsApi } from "../../apiConfiguration";
import { getSchematisationsApi } from "../../apiConfiguration";
import { ExplainSideColumn } from "../../components/ExplainSideColumn";
import { GridCard } from "../../components/GridCard";
import ShowTags from "../../components/ShowTags";
import ThreediModelLink from "../../components/ThreediModelLink";
import { Toast } from "../../components/Toast";
import { useSetOrApplyQueryOrganisation } from "../../hooks/organisationQueryParam";
import {
  useResultFiles,
  useSimulation,
  useSimulationEvents,
  useSimulationStatus,
} from "../../queries/simulations";
import downloadFromUrl from "../../utils/downloadFromUrl";

import DownloadResultsButton from "./DownloadResultsButton";
import InitialConditions from "./InitialConditions";
import SaveAsSimulationTemplateButton from "./SaveAsSimulationTemplateButton";
import SimulationEventsDetails from "./SimulationEventsDetails";
import SimulationPostProcessDetails from "./SimulationPostProcessDetails";

export type SaveSimulationDataType = (
  attributename: string,
  value: Simulation[keyof Simulation],
  isMetaAttribute?: boolean
) => void;

// LIVE_SITE_DURATION is a sentinel value (100 years in seconds) used to indicate
// that a simulation is a "Rana live" simulation without a predefined end time
const LIVE_SITE_DURATION = 3153600000;

const simulationsApi = getSimulationsApi();

const SimulationDetail: FC = () => {
  useSetOrApplyQueryOrganisation();

  const queryClient = useQueryClient();
  const schematisationsApi = getSchematisationsApi();

  const { id } = useParams<{ id: string }>();

  const simulationId = id ? parseInt(id) : undefined;

  const simulationResponse = useSimulation(simulationId);
  const simulation = simulationResponse.data;

  const threediModelId = simulation?.threedimodelId;

  const eventsResponse = useSimulationEvents(simulationId);
  const events: ThreediEvent = eventsResponse.data || {};

  const statusResponse = useSimulationStatus(simulationId);
  const status = statusResponse.data;

  const results = useResultFiles(simulationId);

  const isLiveSimulation = simulation?.duration === LIVE_SITE_DURATION;
  const duration = isLiveSimulation ? status?.time : simulation?.duration ?? status?.time;

  const hasInitial = Object.entries(events).some(([k, v]) => k.startsWith("initial") && v);
  const hasEvents = Object.entries(events).some(
    ([k, v]) => !k.startsWith("initial") && v && v.length
  );
  const has_download_logs_button = results.data
    ? results.data.results
        .map((result) => {
          if (result.filename.startsWith("log_")) {
            if (result && result.file && result.file.size && result.file.size > 0) {
              return true;
            } else {
              return false;
            }
          }
          return false;
        })
        .some((element) => element === true)
    : false;

  const getOffset = (date: Date) => {
    const sign = date.getTimezoneOffset() > 0 ? "-" : "+";
    const offset = Math.abs(date.getTimezoneOffset());

    const pad = (value: number) => (value < 10 ? "0" + value : value);

    const hours = pad(Math.floor(offset / 60));
    const minutes = pad(offset % 60);
    return sign + hours + ":" + minutes;
  };

  const toast = useToast();
  const showToast = (title: string, status: "success" | "error") => {
    toast({
      render: ({ onClose }) => <Toast {...{ title, status, onClose }} />,
      duration: 9000,
      isClosable: true,
    });
  };

  const saveSimulationData: SaveSimulationDataType = async (attributename, value) => {
    if (!simulation) return;
    const data = {
      name: simulation.name ?? "",
      [attributename]: value,
    };

    try {
      await simulationsApi.simulationsPartialUpdate({
        id: simulation && simulation.id ? simulation.id : 0,
        data,
      });
      showToast("Simulation data saved", "success");
      queryClient.invalidateQueries({ queryKey: ["simulation", simulation?.id] });
      queryClient.refetchQueries({ queryKey: ["usages"] });
    } catch (error) {
      showToast("Something went wrong while saving the simulation", "error");
      console.log(error);
    }
  };

  function EditableControls() {
    const { isEditing, getSubmitButtonProps, getCancelButtonProps, getEditButtonProps } =
      useEditableControls();

    return isEditing ? (
      <ButtonGroup justifyContent="center" size="sm">
        <IconButton icon={<CheckIcon />} {...getSubmitButtonProps()} ml={2} aria-label="check" />
        <IconButton icon={<CloseIcon />} {...getCancelButtonProps()} aria-label="close" />
      </ButtonGroup>
    ) : (
      <Flex justifyContent="right">
        <IconButton
          size="sm"
          icon={<EditIcon />}
          color="brand.analytic"
          {...getEditButtonProps()}
          aria-label="edit"
        />
      </Flex>
    );
  }

  const [projectTags, otherTags] = useMemo(() => {
    const projectTags: string[] = [];
    const otherTags: string[] = [];
    if (!simulation || !simulation.tags) return [[], []];
    simulation.tags.forEach((tag) => {
      const firstWord = tag.split(":")[0];
      const isProject = firstWord.toUpperCase() === "PROJECT";
      if (isProject) {
        projectTags.push(tag.split(":").splice(1).join("").trim());
      } else {
        otherTags.push(tag);
      }
    });

    return [projectTags, otherTags];
  }, [simulation]);

  const simulationStatusComponents = {
    error: (
      <Center width="100%" marginTop="18px">
        Simulation not found. It is possible that it exists but you are not authorized to view this
        simulation because it belongs to an organisation that you have no access rights for.
      </Center>
    ),
    pending: (
      <Center width="100%" height="100%">
        <Spinner size="xl" />
      </Center>
    ),
    success: simulation && (
      <Box marginTop="8px">
        <Heading size="lg" marginBottom="8">
          Simulation "{simulation.name}" (id {simulation.id})
        </Heading>
        {status && status.name === "crashed" ? (
          <Flex>
            <Alert status="error" style={{ width: "75%" }} mr="5px">
              <AlertIcon />
              <AlertTitle>
                Note: This is a crashed simulation.{" "}
                {has_download_logs_button ? "Download the error messages here:" : ""}
              </AlertTitle>
            </Alert>

            {results.isSuccess && results.data.results.length > 0 ? (
              <div style={{ alignSelf: "center" }}>
                {has_download_logs_button
                  ? results.data.results.map((result) => {
                      return result.filename.startsWith("log_") ? (
                        <Button
                          variant="secondary"
                          key={result.id}
                          onClick={async (event) => {
                            event.preventDefault();
                            if (!simulation.id) return;
                            const url = await schematisationsApi.simulationsResultsFilesDownload({
                              id: result.id || 0,
                              simulationPk: simulation.id,
                            });
                            if (url && url.getUrl) {
                              downloadFromUrl(url.getUrl, result.filename || "");
                            }
                          }}
                        >
                          Download
                        </Button>
                      ) : null;
                    })
                  : null}
              </div>
            ) : null}
          </Flex>
        ) : null}

        <Grid gap="1rem" templateColumns="repeat(4, 1fr)">
          <GridCard colSpan={2} title="Results">
            <DownloadResultsButton simulation={simulation} />
          </GridCard>
          <GridCard colSpan={2} title="Name">
            <Editable
              textAlign="center"
              defaultValue={`${simulation.name}`}
              isPreviewFocusable={false}
              onSubmit={(name) => saveSimulationData("name", name)}
              submitOnBlur={false}
            >
              <Flex>
                <EditablePreview mr={2} />
                <Input as={EditableInput} />
                <EditableControls />
              </Flex>
            </Editable>
          </GridCard>
          <GridCard colSpan={2} title="Model">
            <ThreediModelLink threediModelId={threediModelId} />
          </GridCard>
          <GridCard colSpan={2} title="Duration">
            <p>{duration ?? "-"}s</p>
          </GridCard>
          <GridCard colSpan={2} title="Tags">
            <ShowTags
              tags={otherTags}
              isEditable
              saveData={(attributename: string, tags: Simulation[keyof Simulation]) =>
                saveSimulationData(
                  attributename,
                  Array.isArray(tags) ? [...projectTags, ...tags] : tags
                )
              }
            />
          </GridCard>

          <GridCard title="Start">
            <p>
              {simulation.startDatetime?.toLocaleString()} (UTC{" "}
              {getOffset(simulation.startDatetime)})
            </p>
          </GridCard>
          <GridCard title="End">
            <p>
              {simulation.endDatetime?.toLocaleString()} (UTC {getOffset(simulation.startDatetime)})
            </p>
          </GridCard>
          {simulation ? (
            <GridCard title="">
              <SaveAsSimulationTemplateButton simulation={simulation} />
            </GridCard>
          ) : null}
        </Grid>
      </Box>
    ),
  };

  return (
    <ExplainSideColumn
      imgUrl={SimulationsSvg}
      imgAltDescription={"Simulation icon"}
      headerText={"Simulation"}
      explanationText={"An overview of a simulation, its initial conditions and events."}
      backUrl={"/"}
    >
      {simulationStatusComponents[simulationResponse.status]}
      <Box w="full">
        {simulation && hasInitial ? <InitialConditions events={events}></InitialConditions> : null}
        {simulation && hasEvents && duration ? (
          <SimulationEventsDetails duration={duration} events={events} />
        ) : null}
        {simulationId ? (
          <SimulationPostProcessDetails
            simulationName={simulation?.name}
            simulationPk={simulationId}
          />
        ) : null}
      </Box>
    </ExplainSideColumn>
  );
};

export default SimulationDetail;
